// storyPapers.js
import {
  STAGE_CLASS_MAP,
  STORY_META_MAP,
  STORY_STAGE_META,
} from '../constants';

export function mountStoryPapers({
  modal,
  stageTplMap = {},
  paperTplId = 'tpl-story-paper',
  stageOrder = [],
  onOpen,
  onClose,
  onActiveChange,
} = {}) {
  if (!modal) return;

  const stack = modal?.querySelector('.story-modal_stack') || modal;
  const paperTpl = document.getElementById(paperTplId);

  const openPapers = new Map();
  let isOpen = false;

  let currentGroupStageId = null;

  function getNextStageId(stageId) {
    const n = stageOrder.length;
    if (!n) return null;

    const idx = stageOrder.indexOf(stageId);
    if (idx < 0) return stageOrder[0];

    return stageOrder[(idx + 1) % n];
  }

  function activePaper(target) {
    if (target.classList.contains('active')) return;

    for (const p of openPapers.values()) {
      if (!p.classList.contains('active')) continue;
      p.classList.remove('active');
      onActiveChange?.(p.dataset.stageId, p.dataset.paperId, p, false);
    }
    target.classList.add('active');
    onActiveChange?.(
      target.dataset.stageId,
      target.dataset.paperId,
      target,
      true,
    );
  }

  function ensurePaper(paperId = 'main') {
    if (openPapers.has(paperId)) return openPapers.get(paperId);

    if (!paperTpl)
      throw new Error(`[mountStoryPapers] missing template: #${paperTplId}`);

    const frag = paperTpl.content.cloneNode(true);
    const paper = frag.querySelector('[data-modal]');

    if (!paper)
      throw new Error('[mountStoryPapers] template must include [data-modal]');

    paper.dataset.paperId = paperId;

    const isMain = paperId === 'main';

    if (!isMain) {
      paper.classList.add('sub');
    }

    const titleEl = paper.querySelector('.story-modal_title');
    if (titleEl) {
      const titleId = `story-modal-title-${paperId}`;
      titleEl.id = titleId;
      paper.setAttribute('aria-labelledby', titleId);
    }

    stack.appendChild(paper);
    openPapers.set(paperId, paper);

    return paper;
  }

  function clearStageClasses(paper) {
    paper.classList.forEach((cls) => {
      if (cls.startsWith('stage-')) paper.classList.remove(cls);
    });
  }

  function applyStageClass(paper, stageId) {
    clearStageClasses(paper);

    const cls = STAGE_CLASS_MAP?.[stageId];
    if (cls) paper.classList.add(cls);
  }

  function applyStoryMeta(paper, stageId) {
    const meta = STORY_META_MAP?.[stageId];

    const titleEl = paper.querySelector('.story-modal_title');
    const tagEl = paper.querySelector('.story-tag');

    if (titleEl) {
      renderTitle(titleEl, meta);
    }

    if (tagEl) {
      const tagText = meta?.tag;
      if (tagText) {
        tagEl.textContent = tagText;
        tagEl.hidden = false;
      } else {
        tagEl.textContent = '';
        tagEl.hidden = true;
      }
    }
  }

  function hydrateMedia(root) {
    const videos = root.querySelectorAll('video');
    requestAnimationFrame(() => {
      videos.forEach((v) => {
        const poster = v.getAttribute('poster');
        if (poster) v.poster = poster;

        v.load();
      });
    });
  }

  function renderStage(paper, stageId) {
    const doc = paper.querySelector('.story-doc');
    if (!doc) return;

    const tplId = stageTplMap?.[stageId];
    const tpl = tplId ? document.getElementById(tplId) : null;

    const node = tpl
      ? tpl.content.cloneNode(true)
      : document.createTextNode('no content');

    doc.replaceChildren(node);

    hydrateMedia(doc);
  }

  function renderTitle(titleEl, meta) {
    titleEl.replaceChildren();

    const parts = meta?.titleParts;

    if (Array.isArray(parts) && parts.length) {
      for (const p of parts) {
        const node = document.createElement(p.italic ? 'em' : 'span');
        node.textContent = p.text ?? '';

        if (p.className) node.classList.add(p.className);

        titleEl.appendChild(node);

        if (p.br) {
          titleEl.appendChild(document.createElement('br'));
        }
      }
      return;
    }
    titleEl.textContent = meta?.title || '';
  }

  function open(stageId, { paperId = 'main' } = {}) {
    const paper = ensurePaper(paperId);

    paper.dataset.stageId = stageId;

    applyStageClass(paper, stageId);
    applyStoryMeta(paper, stageId);

    modal.classList.add('open');
    isOpen = true;

    requestAnimationFrame(() => {
      renderStage(paper, stageId);
      onOpen?.(stageId, paperId, paper);
    });

    return paper;
  }

  function openGroup(stageId) {
    currentGroupStageId = stageId;

    const group = STORY_STAGE_META?.[stageId]?.opens;
    const ids = Array.isArray(group) && group.length ? group : [stageId];

    let activeIdx = ids.indexOf(stageId);
    if (activeIdx < 0) activeIdx = 0;

    ids.forEach((id, idx) => {
      const pid = idx === 0 ? 'main' : `detail-${idx}`;
      const paper = open(id, {
        paperId: pid,
      });

      if (idx === activeIdx) activePaper(paper);
    });
  }

  function waitTransition(el, ms = 220) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (e) => {
        if (e.target === el) finish();
      };
      el.addEventListener('transitionend', onEnd);
      setTimeout(finish, ms);
    });
  }

  async function close({ removePapers = true } = {}) {
    if (!isOpen) return;

    modal.classList.add('closing');
    modal.classList.remove('open');
    isOpen = false;

    onClose?.();

    await waitTransition(modal, 220);

    modal.classList.remove('closing');

    if (removePapers) {
      for (const paper of openPapers.values()) paper.remove();
      openPapers.clear();
    }
  }

  function isOpened() {
    return isOpen;
  }

  function onPaperPointerDown(e) {
    if (!isOpen) return;

    const paper = e.target.closest('[data-modal]');
    if (!paper) return;

    e.stopPropagation();

    activePaper(paper);
  }

  function onPointerDown(e) {
    if (!isOpen) return;

    for (const paper of openPapers.values()) {
      if (paper.contains(e.target)) return;
    }
    close();
  }

  function onKeyDown(e) {
    if (!isOpen) return;
    if (e.key === 'Escape') close();
  }

  async function onNextClick(e) {
    if (!isOpen) return;

    const btn = e.target.closest('.btn-next');
    if (!btn || !modal.contains(btn)) return;

    e.preventDefault();
    e.stopPropagation();

    const nextStageId = getNextStageId(currentGroupStageId);
    if (!nextStageId) return;

    await close({ removePapers: true });
    openGroup(nextStageId);
  }

  modal.addEventListener('pointerdown', onPaperPointerDown);
  modal.addEventListener('pointerdown', onPointerDown);
  modal.addEventListener('click', onNextClick, true);
  document.addEventListener('keydown', onKeyDown);

  function destroy() {
    close();
    modal.removeEventListener('pointerdown', onPaperPointerDown);
    modal.removeEventListener('pointerdown', onPointerDown);
    modal.removeEventListener('click', onNextClick, true);
    document.removeEventListener('keydown', onKeyDown);
  }

  return { open, openGroup, close, isOpened, destroy };
}
